import {
  Button,
  Flex,
  Form,
  Heading,
  Modal,
  Paragraph,
  Skeleton,
  useToast,
} from "@ir/ui";
import { useNavigate } from "react-router";
import { PageHeader } from "@src/presentation/components/Layout/common";
import { type BreadcrumbItem } from "@src/presentation/components/common";
import { useMemo, useState } from "react";
import type { Brand } from "@src/domain/entities/brand";
import { Brand as BrandEntity } from "@src/domain/entities/brand";
import { normalizeBrandLogoFile } from "@src/domain/dto/brands/UpdateBrand.dto";
import { BrandsAddEditForm, type BrandFormValues } from "./BrandsAddEditForm";
import { useUpdateBrand } from "./useUpdateBrand";
import type { UpdateBrandPayload } from "./useUpdateBrand";
import {
  isBrandKeyDuplicateError,
  BRAND_KEY_ALREADY_EXISTS_MESSAGE,
} from "./brandKeyValidation";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import { Permissions } from "@src/domain/permissions/Permissions";

function buildUpdateBrandPayload(
  base: UpdateBrandPayload,
  newLogoFile: File | null | undefined,
  removeLogo: boolean,
): UpdateBrandPayload {
  if (newLogoFile) {
    console.log("newLogoFile", newLogoFile);
    return { ...base, brandLogoFile: newLogoFile };
  }
  if (removeLogo) {
    return { ...base, brandLogoRemove: true };
  }
  console.log("base", base);
  return base;
}

function clientBrandAfterSave(
  previous: Brand | null,
  updated: Brand,
  opts: { logoRemoved: boolean; newFileUploaded: boolean },
): Brand {
  const logoUrl = (() => {
    if (opts.logoRemoved && !opts.newFileUploaded) return null;
    if (opts.newFileUploaded) return updated.brandLogoUrl;
    const fromApi = updated.brandLogoUrl?.trim() ?? "";
    if (fromApi.length > 0) return updated.brandLogoUrl;
    return previous?.brandLogoUrl ?? null;
  })();

  return new BrandEntity({
    brandId: updated.brandId,
    brandKey: updated.brandKey,
    brandName: updated.brandName,
    brandDisplayName: updated.brandDisplayName,
    brandDescription: updated.brandDescription,
    brandLogoUrl: logoUrl,
    brandIsActive: updated.brandIsActive,
    brandCreatedAt: updated.brandCreatedAt ?? previous?.brandCreatedAt ?? null,
    brandUpdatedAt: updated.brandUpdatedAt ?? previous?.brandUpdatedAt ?? null,
    brandDeletedAt: updated.brandDeletedAt ?? previous?.brandDeletedAt ?? null,
    brandCreatedBy: updated.brandCreatedBy ?? previous?.brandCreatedBy ?? null,
    brandUpdatedBy: updated.brandUpdatedBy ?? previous?.brandUpdatedBy ?? null,
    brandDeletedBy: updated.brandDeletedBy ?? previous?.brandDeletedBy ?? null,
    brandFeaturesCount:
      updated.brandFeaturesCount ?? previous?.brandFeaturesCount ?? null,
    brandPurchasersCount:
      updated.brandPurchasersCount ?? previous?.brandPurchasersCount ?? null,
    brandFeatures: previous?.brandFeatures ?? updated.brandFeatures ?? null,
    brandPurchasers:
      previous?.brandPurchasers ?? updated.brandPurchasers ?? null,
    brandUsers: previous?.brandUsers ?? updated.brandUsers ?? null,
  });
}

export interface BrandDetailsPageHeaderProps {
  brand: Brand | null;
  organizationId: number;
  organizationKey: string;
  headerTitle: string;
  subtitle: string;
  brandLoading: boolean;
  breadcrumbItems: BreadcrumbItem[];
  onBrandSaved?: (brand: Brand) => Promise<void> | void;
}

export const BrandDetailsPageHeader = ({
  brand,
  organizationId,
  organizationKey,
  headerTitle,
  subtitle,
  brandLoading,
  breadcrumbItems,
  onBrandSaved,
}: BrandDetailsPageHeaderProps) => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isFormSubmittable, setIsFormSubmittable] = useState(false);
  const [form] = Form.useForm<BrandFormValues>();
  const updateBrand = useUpdateBrand();
  const { success: successToast, error: errorToast } = useToast();

  const initialValues = useMemo<BrandFormValues>(
    () => ({
      brandId: brand?.brandId ?? null,
      organizationId,
      organizationKey,
      brandKey: brand?.brandKey ?? "",
      brandName: brand?.brandName ?? "",
      brandDisplayName: brand?.brandDisplayName ?? brand?.brandName ?? "",
      brandDescription: brand?.brandDescription ?? "",
      brandLogoFile: null,
      removeBrandLogo: false,
      brandIsActive: brand?.brandIsActive ?? true,
    }),
    [brand, organizationId, organizationKey],
  );

  const handleSubmit = async (values: BrandFormValues) => {
    setIsSaving(true);
    try {
      const newLogoFile = normalizeBrandLogoFile(values.brandLogoFile);
      const removeLogo = Boolean(
        values.removeBrandLogo ?? form.getFieldValue("removeBrandLogo"),
      );

      const base: UpdateBrandPayload = {
        brandId: brand?.brandId ?? null,
        organizationId,
        organizationKey,
        brandKey: values.brandKey?.trim() ?? "",
        brandName: values.brandName?.trim() ?? "",
        brandDisplayName: values.brandDisplayName?.trim() ?? "",
        brandDescription: values.brandDescription?.trim() || null,
        brandIsActive: Boolean(values.brandIsActive),
      };

      const payload = buildUpdateBrandPayload(base, newLogoFile, removeLogo);
      const updated = await updateBrand(payload);
      const next = clientBrandAfterSave(brand, updated, {
        logoRemoved: removeLogo,
        newFileUploaded: Boolean(newLogoFile),
      });
      await onBrandSaved?.(next);
      successToast({ content: "Brand updated successfully." });
      setIsFormSubmittable(false);
      setIsModalOpen(false);
    } catch (saveError: unknown) {
      console.error("Failed to update brand", saveError);
      if (isBrandKeyDuplicateError(saveError)) {
        form.setFields([
          {
            name: "brandKey",
            errors: [BRAND_KEY_ALREADY_EXISTS_MESSAGE],
          },
        ]);
        setIsFormSubmittable(false);
      }
      errorToast({
        content: "Failed to update brand. Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <PageHeader breadcrumb={{ items: breadcrumbItems }}>
        <Flex align="center" gap={0.5} justifyContent="space-between">
          <Flex direction="column">
            <Heading level={5} className="text-grey-700 font-bold m-0">
              {brandLoading ? (
                <Skeleton
                  height={24}
                  width={400}
                  animation="wave"
                  variant="rounded"
                />
              ) : (
                headerTitle || "Brand"
              )}
            </Heading>
            <Paragraph className="text-grey-500 font-regular m-0">
              {subtitle}
            </Paragraph>
          </Flex>
          <Flex direction="row" gap={0.5} align="end">
            <HasAnyPermission
              requiredPermissions={[Permissions.MASTER_DATA_VIEW]}
            >
              <Button
                size="small"
                variant="outline"
                onPress={() => {
                  const resolvedBrandKey = brand?.brandKey;
                  if (!organizationKey || !resolvedBrandKey) {
                    return;
                  }
                  navigate(
                    `/master-data/${organizationKey}/${resolvedBrandKey}`,
                  );
                }}
                isDisabled={
                  brandLoading || !brand?.brandKey || !organizationKey
                }
              >
                Master Data
              </Button>
            </HasAnyPermission>
            <HasAnyPermission requiredPermissions={[Permissions.BRANDS_EDIT]}>
              <Button
                size="small"
                variant="outline"
                onPress={() => {
                  setIsFormSubmittable(false);
                  setIsModalOpen(true);
                }}
                isDisabled={brandLoading || !brand?.brandId}
              >
                Edit Brand
              </Button>
            </HasAnyPermission>
          </Flex>
        </Flex>
      </PageHeader>
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setIsFormSubmittable(false);
        }}
        size="large"
        classNames={{
          body: "organization-details-page-header-modal-content",
          header: "organization-details-page-header-modal-header",
        }}
        headerContent={
          <Heading level={6} className="text-grey-700 font-bold m-0">
            Edit Brand
          </Heading>
        }
        footerContent={
          <Flex gap={0.5}>
            <Button
              size="small"
              onPress={() => setIsModalOpen(false)}
              color="grey"
              isDisabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              size="small"
              color="primary"
              onPress={() => form.submit()}
              isLoading={isSaving}
              isDisabled={isSaving || !isFormSubmittable}
            >
              Save
            </Button>
          </Flex>
        }
      >
        <BrandsAddEditForm
          form={form}
          mode="edit"
          initialValues={initialValues}
          existingLogoUrl={brand?.brandLogoUrl ?? null}
          excludeBrandKey={brand?.brandKey}
          onSubmit={handleSubmit}
          onFormValidityChange={setIsFormSubmittable}
        />
      </Modal>
    </>
  );
};
